/**
 * Constants for ModifyFieldNode
 * Uses FilterType and ModifyType from types/enums.ts
 *
 * Field mappings (FE → BE):
 * - Source = filter.source (node ID of a previous step in the flow - selected dynamically)
 * - Operator = filter.type (FilterType enum values)
 * - Value = filter.params (condition_value)
 * - Modification = change.type (ModifyType enum values)
 * - Output = change.params (value_to_change)
 *
 * Note: Source options are generated dynamically from nodes BEFORE the current node in the same flow.
 */

import { FilterType, ModifyType } from "types/enums";

/**
 * Operator options - how to compare the value (condition type)
 * Maps to filter.type / FilterType
 */
export const MODIFY_OPERATOR_OPTIONS = [
    { value: FilterType.Always, labelKey: "nodes.modify.forms.operator_options.always" },
    { value: FilterType.Equals, labelKey: "nodes.modify.forms.operator_options.equals" },
    { value: FilterType.NotEquals, labelKey: "nodes.modify.forms.operator_options.not_equals" },
    { value: FilterType.IsTrue, labelKey: "nodes.modify.forms.operator_options.is_true" },
    { value: FilterType.IsFalse, labelKey: "nodes.modify.forms.operator_options.is_false" },
    { value: FilterType.IsLessThan, labelKey: "nodes.modify.forms.operator_options.smaller_than" },
    { value: FilterType.IsLessThanOrEquals, labelKey: "nodes.modify.forms.operator_options.smaller_than_or_equals" },
    { value: FilterType.IsGreaterThan, labelKey: "nodes.modify.forms.operator_options.bigger_than" },
    { value: FilterType.IsGreaterThanOrEquals, labelKey: "nodes.modify.forms.operator_options.bigger_than_or_equals" },
    { value: FilterType.IsBetween, labelKey: "nodes.modify.forms.operator_options.between" },
    { value: FilterType.IsBetweenEquals, labelKey: "nodes.modify.forms.operator_options.between_include" },
    { value: FilterType.Contains, labelKey: "nodes.modify.forms.operator_options.string_contains" },
    { value: FilterType.NotContains, labelKey: "nodes.modify.forms.operator_options.string_not_contains" },
    { value: FilterType.StartsWith, labelKey: "nodes.modify.forms.operator_options.string_starts_with" },
    { value: FilterType.EndsWith, labelKey: "nodes.modify.forms.operator_options.string_ends_with" },
    { value: FilterType.IsEmpty, labelKey: "nodes.modify.forms.operator_options.is_empty" },
    { value: FilterType.IsNotEmpty, labelKey: "nodes.modify.forms.operator_options.is_not_empty" },
    { value: FilterType.RegexMatch, labelKey: "nodes.modify.forms.operator_options.regex_match" },
    { value: FilterType.RegexNotMatch, labelKey: "nodes.modify.forms.operator_options.regex_not_match" },
] as const;

/**
 * Modification options - what to do with the value (change type)
 * Maps to change.type / ModifyType
 */
export const MODIFY_MODIFICATION_OPTIONS = [
    { value: ModifyType.Replace, labelKey: "nodes.modify.forms.modification_options.replace" },
    { value: ModifyType.Math, labelKey: "nodes.modify.forms.modification_options.math" },
    { value: ModifyType.AddPrefix, labelKey: "nodes.modify.forms.modification_options.add_prefix" },
    { value: ModifyType.AddPostfix, labelKey: "nodes.modify.forms.modification_options.add_postfix" },
    { value: ModifyType.ToFloat, labelKey: "nodes.modify.forms.modification_options.to_float" },
    { value: ModifyType.ToBool, labelKey: "nodes.modify.forms.modification_options.to_bool" },
    { value: ModifyType.ToInt, labelKey: "nodes.modify.forms.modification_options.to_int" },
    { value: ModifyType.DateToUnix, labelKey: "nodes.modify.forms.modification_options.date_to_unix" },
    { value: ModifyType.DoNotSend, labelKey: "nodes.modify.forms.modification_options.do_not_send" },
    { value: ModifyType.CzNinToBirthdate, labelKey: "nodes.modify.forms.modification_options.cz_nin_to_birthdate" },
    { value: ModifyType.CzNinToGender, labelKey: "nodes.modify.forms.modification_options.cz_nin_to_gender" },
    { value: ModifyType.ToBase64, labelKey: "nodes.modify.forms.modification_options.to_base64" },
    { value: ModifyType.FromBase64, labelKey: "nodes.modify.forms.modification_options.from_base64" },
    { value: ModifyType.FirstRegexMatch, labelKey: "nodes.modify.forms.modification_options.first_regex_match" },
    { value: ModifyType.FormatDate, labelKey: "nodes.modify.forms.modification_options.format_date" },
    { value: ModifyType.ModifyDate, labelKey: "nodes.modify.forms.modification_options.modify_date" },
    { value: ModifyType.RegexReplace, labelKey: "nodes.modify.forms.modification_options.regex_replace" },
] as const;
